import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import SEO from "@/components/SEO";

/**
 * Privacy Policy — real, brand-specific content covering data collected on the
 * Star Jewels storefront, how it is used, third-party processors (Razorpay,
 * Google Sign-in, Gemini AI), cookies, retention, and user rights.
 */
export default function Privacy() {
  return (
    <main className="mx-auto max-w-3xl px-5 sm:px-10 py-14 sm:py-20" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="Star Jewels Privacy Policy — what data we collect, why, how it's stored, third-party processors we work with (Razorpay for payments, Google for sign-in, Gemini for the Moon chatbot), your rights."
        path="/privacy"
      />
      <div className="overline text-center">Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-light text-center mt-2 leading-tight">Privacy Policy</h1>
      <p className="text-sm text-sj-muted text-center mt-3">Last updated: 6 February 2026</p>

      <div className="mt-10 space-y-8 text-sj-inkSoft leading-relaxed">
        <section>
          <p className="p-5 bg-sj-cream rounded-md flex items-start gap-3 not-italic">
            <Lock size={18} className="text-sj-goldDark mt-0.5 shrink-0" />
            <span>
              Star Jewels ("we", "us") respects your privacy. This policy explains what personal information we collect
              when you visit <strong>shopstarjewels.com</strong>, why we collect it, who we share it with, and the
              choices you have. If you have any question, write to us at <strong>officialstarjewels@gmail.com</strong>.
            </span>
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">1. What we collect</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li><strong>Order &amp; account data</strong> — name, email, phone number, shipping address, order history.</li>
            <li><strong>Payment metadata</strong> — payment status and Razorpay order/transaction IDs. We do <em>not</em> receive or store your card number, CVV, UPI PIN or net-banking credentials — those are handled by Razorpay.</li>
            <li><strong>Sign-in identifier</strong> — if you sign in with Google, we receive your name, email and profile picture from Google (no password).</li>
            <li><strong>Conversation data</strong> — messages you send to Moon, our on-site AI stylist, are processed by Google's Gemini API to generate a response.</li>
            <li><strong>Technical data</strong> — approximate location (city-level from IP), device/browser type, pages visited, session cookies — used for analytics, security, and to keep you signed in.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">2. Why we use it</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>To fulfil your order — process payment, ship your purchase, and email tracking updates.</li>
            <li>To offer post-purchase support and handle exchange requests.</li>
            <li>To personalise your experience — remembering your cart, showing relevant pieces, and letting Moon give you helpful styling suggestions.</li>
            <li>To send occasional emails about new drops, offers or updates <em>only</em> if you've opted in via our Newsletter.</li>
            <li>To detect fraud, secure the site, and improve our catalogue and checkout.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">3. Who we share it with</h2>
          <p className="mt-2">We share the minimum data required with the third parties below. We <strong>never sell</strong> your personal information.</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li><strong>Razorpay Software Pvt. Ltd.</strong> — processes online payments. Governed by their <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-sj-goldDark underline underline-offset-4">Privacy Policy</a>.</li>
            <li><strong>Google (Sign-in with Google &amp; Gemini AI)</strong> — verifies your identity if you choose Google sign-in, and powers Moon's responses. Governed by the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sj-goldDark underline underline-offset-4">Google Privacy Policy</a>.</li>
            <li><strong>Courier partners</strong> — receive your shipping address, name and phone number to deliver your order.</li>
            <li><strong>Cloud hosting / storage</strong> — our website and product images are hosted on secure cloud infrastructure with encryption in transit (TLS) and at rest.</li>
            <li><strong>Law enforcement</strong> — only when required by a lawful order.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">4. Cookies &amp; local storage</h2>
          <p className="mt-2">We use a small number of first-party cookies and browser local-storage entries to keep you signed in,
          remember your cart, and understand overall site performance. We do not use invasive third-party
          advertising trackers. You can clear cookies from your browser at any time — some checkout and
          sign-in features may then need to be repeated.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">5. Data retention</h2>
          <p className="mt-2">Order and invoice data is retained for as long as required by Indian tax and consumer-protection law
          (typically 7 years). Account data is retained while your account is active. If you'd like your
          account deleted, email <strong>officialstarjewels@gmail.com</strong> and we will delete or anonymise
          all personal data associated with it within 30 days, subject to any legal retention we must keep.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">6. Your rights</h2>
          <p className="mt-2">You can:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Request a copy of the personal data we hold about you.</li>
            <li>Ask us to correct information that is inaccurate or incomplete.</li>
            <li>Ask us to delete your account and associated personal data.</li>
            <li>Withdraw newsletter consent at any time via the unsubscribe link or by writing to us.</li>
          </ul>
          <p className="mt-2">To exercise any of these, please email <strong>officialstarjewels@gmail.com</strong> from the address on file.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">7. Security</h2>
          <p className="mt-2">We use TLS/HTTPS across the site, hash passwords with a modern algorithm, and restrict access to
          personal data to authorised team members only. No transmission over the internet is 100% secure,
          but we take every reasonable step to protect your information.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">8. Children's privacy</h2>
          <p className="mt-2">Our site and products are intended for buyers aged 18 and above. We do not knowingly collect
          personal information from anyone under 18. If you believe a minor has provided us with data,
          please contact us and we will delete it.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">9. Changes to this policy</h2>
          <p className="mt-2">We may update this policy from time to time. The current version is always available at
          <strong> shopstarjewels.com/privacy</strong>. Substantial changes will be highlighted on the site or
          notified by email.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">10. Contact</h2>
          <p className="mt-2">Star Jewels · <strong>officialstarjewels@gmail.com</strong> · Instagram
          <a href="https://instagram.com/officialstarjewels" target="_blank" rel="noopener noreferrer" className="text-sj-goldDark underline underline-offset-4 ml-1">@officialstarjewels</a>.
          For any privacy-specific concern please write "PRIVACY REQUEST" in the subject line so we can prioritise it.</p>
        </section>

        <div className="pt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-sj-goldDark underline underline-offset-4">Terms &amp; Conditions</Link>
          <Link to="/return-policy" className="text-sj-goldDark underline underline-offset-4">Return &amp; Exchange Policy</Link>
        </div>
      </div>
    </main>
  );
}
